import * as THREE from 'three';

/**
 * Input state for keyboard keys.
 * Tracks both current state and previous state to detect "just pressed" transitions.
 */
interface KeyState {
  down: boolean;
  downPrevious: boolean;
}

/**
 * Input state for mouse buttons.
 */
interface MouseButtonState {
  down: boolean;
  downPrevious: boolean;
}

/**
 * Input Manager for keyboard and mouse input.
 *
 * Tracks key/button states and provides clean query API for:
 * - Key down/pressed states
 * - Mouse position (screen and world coordinates)
 * - Mouse button states
 *
 * Call update() once per frame to maintain "just pressed" state.
 */
export class InputManager {
  private keys: Map<string, KeyState> = new Map();
  private mouseButton: MouseButtonState = { down: false, downPrevious: false };
  private mouseScreenX: number = 0;
  private mouseScreenY: number = 0;
  private camera: THREE.OrthographicCamera;

  constructor(camera: THREE.OrthographicCamera, canvas: HTMLElement) {
    this.camera = camera;

    // Register keyboard event listeners
    window.addEventListener('keydown', (e) => this.onKeyDown(e));
    window.addEventListener('keyup', (e) => this.onKeyUp(e));

    // Register mouse event listeners
    canvas.addEventListener('mousemove', (e) => this.onMouseMove(e));
    canvas.addEventListener('mousedown', (e) => this.onMouseDown(e));
    canvas.addEventListener('mouseup', (e) => this.onMouseUp(e));
  }

  /**
   * Update input state for the current frame.
   * Call this once per frame before processing input.
   */
  update(): void {
    // Update previous state for all keys
    for (const state of this.keys.values()) {
      state.downPrevious = state.down;
    }

    // Update previous state for mouse button
    this.mouseButton.downPrevious = this.mouseButton.down;
  }

  /**
   * Check if a key is currently held down.
   */
  isKeyDown(key: string): boolean {
    const state = this.keys.get(key.toLowerCase());
    return state ? state.down : false;
  }

  /**
   * Check if a key was just pressed this frame (transition from up to down).
   */
  isKeyPressed(key: string): boolean {
    const state = this.keys.get(key.toLowerCase());
    return state ? (state.down && !state.downPrevious) : false;
  }

  /**
   * Check if the left mouse button is currently held down.
   */
  isMouseDown(): boolean {
    return this.mouseButton.down;
  }

  /**
   * Check if the left mouse button was just pressed this frame.
   */
  isMousePressed(): boolean {
    return this.mouseButton.down && !this.mouseButton.downPrevious;
  }

  /**
   * Get mouse position in screen coordinates.
   */
  getMouseScreenPosition(): { x: number; y: number } {
    return { x: this.mouseScreenX, y: this.mouseScreenY };
  }

  /**
   * Get mouse position in world coordinates (on the XZ plane).
   */
  getMouseWorldPosition(): { x: number; y: number } {
    return screenToWorld(
      this.mouseScreenX,
      this.mouseScreenY,
      this.camera
    );
  }

  private onKeyDown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    let state = this.keys.get(key);

    if (!state) {
      state = { down: false, downPrevious: false };
      this.keys.set(key, state);
    }

    state.down = true;
  }

  private onKeyUp(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    const state = this.keys.get(key);

    if (state) {
      state.down = false;
    }
  }

  private onMouseMove(event: MouseEvent): void {
    this.mouseScreenX = event.clientX;
    this.mouseScreenY = event.clientY;
  }

  private onMouseDown(event: MouseEvent): void {
    if (event.button === 0) {
      // Left button
      this.mouseButton.down = true;
    }
  }

  private onMouseUp(event: MouseEvent): void {
    if (event.button === 0) {
      // Left button
      this.mouseButton.down = false;
    }
  }
}

/**
 * Convert screen coordinates to world coordinates on the Y=0 plane.
 *
 * The camera looks at the XZ plane (Y is up in Three.js) from an angle.
 * This function casts a ray from the camera through the mouse position
 * and finds where it intersects the Y=0 ground plane.
 *
 * @param screenX - Mouse X position in screen pixels
 * @param screenY - Mouse Y position in screen pixels
 * @param camera - Orthographic camera viewing the scene
 * @returns World position as {x, y} where x/y map to Three.js X/Z axes
 */
export function screenToWorld(
  screenX: number,
  screenY: number,
  camera: THREE.OrthographicCamera
): { x: number; y: number } {
  // Convert screen coordinates to normalized device coordinates (-1 to 1)
  const ndcX = (screenX / window.innerWidth) * 2 - 1;
  const ndcY = -(screenY / window.innerHeight) * 2 + 1;

  // For orthographic camera, we create a ray from the near plane point
  // in the direction the camera is looking
  const nearPoint = new THREE.Vector3(ndcX, ndcY, -1);
  const farPoint = new THREE.Vector3(ndcX, ndcY, 1);

  // Unproject both points to world space
  nearPoint.unproject(camera);
  farPoint.unproject(camera);

  // Get the ray direction
  const rayDir = new THREE.Vector3().subVectors(farPoint, nearPoint).normalize();

  // Find intersection with Y=0 plane
  // Ray: P = nearPoint + t * rayDir
  // Plane: Y = 0
  // Solve for t: nearPoint.y + t * rayDir.y = 0
  // t = -nearPoint.y / rayDir.y
  if (Math.abs(rayDir.y) < 0.0001) {
    // Ray is parallel to ground plane, use near point's XZ
    return { x: nearPoint.x, y: nearPoint.z };
  }

  const t = -nearPoint.y / rayDir.y;
  const intersectX = nearPoint.x + t * rayDir.x;
  const intersectZ = nearPoint.z + t * rayDir.z;

  // Return x and z as our 2D game coordinates (z becomes game y)
  return {
    x: intersectX,
    y: intersectZ,
  };
}
